/**
 * Autofill: sources, formatting, and the auto/manual switch.
 *
 * A bound slot is in **auto** mode by default — its value is regenerated
 * from the linked parties/properties whenever they change. The user can
 * switch any bound slot to **manual**; its text is then theirs and is
 * never overwritten. The manual set travels inside `values` under a
 * reserved key so it survives reloads and moves with the document.
 */
import { VALIDATION_OVERRIDES_KEY } from './validation.js';
import { formatSwedishLegalId } from './legal-id.js';
/** Reserved `values` key holding the slot keys switched to manual entry. */
export const AUTOFILL_MANUAL_KEY = '__autofill_manual';
/**
 * The legacy template lane's key, which only ever held `property_list`
 * fields. Still read so a document carried over from the legacy lane keeps
 * its manual choices; never written.
 */
export const LEGACY_PROPERTY_LIST_MANUAL_KEY = '__property_list_manual';
export const RESERVED_VALUE_KEYS = [
    AUTOFILL_MANUAL_KEY,
    LEGACY_PROPERTY_LIST_MANUAL_KEY,
    VALIDATION_OVERRIDES_KEY,
];
/** True for `values` keys that are bookkeeping, not slot answers. */
export const isReservedValueKey = (key) => key.startsWith('__');
const stringList = (raw) => Array.isArray(raw) ? raw.filter((k) => typeof k === 'string') : [];
/** The slot keys in manual mode — the union of the current and the legacy key. */
export function readManualKeys(values) {
    return new Set([
        ...stringList(values?.[AUTOFILL_MANUAL_KEY]),
        ...stringList(values?.[LEGACY_PROPERTY_LIST_MANUAL_KEY]),
    ]);
}
export function autofillMode(values, key) {
    return readManualKeys(values).has(key) ? 'manual' : 'auto';
}
/**
 * Returns a copy of `values` with `key` switched to `mode`. Switching back
 * to auto also drops the key from the legacy list, so the union read stays
 * truthful. The caller re-runs `resolveAutofill` to refill an auto field.
 */
export function setAutofillMode(values, key, mode) {
    const next = { ...values };
    const current = new Set(stringList(values[AUTOFILL_MANUAL_KEY]));
    if (mode === 'manual')
        current.add(key);
    else
        current.delete(key);
    if (current.size > 0)
        next[AUTOFILL_MANUAL_KEY] = [...current].sort();
    else
        delete next[AUTOFILL_MANUAL_KEY];
    if (mode === 'auto' && LEGACY_PROPERTY_LIST_MANUAL_KEY in values) {
        const legacy = stringList(values[LEGACY_PROPERTY_LIST_MANUAL_KEY]).filter((k) => k !== key);
        if (legacy.length > 0)
            next[LEGACY_PROPERTY_LIST_MANUAL_KEY] = legacy;
        else
            delete next[LEGACY_PROPERTY_LIST_MANUAL_KEY];
    }
    return next;
}
const clean = (s) => {
    const trimmed = s?.trim();
    return trimmed ? trimmed : null;
};
/** "<postnummer> <ort>", or null. */
export function formatPostalTown(address) {
    if (!address)
        return null;
    return clean([clean(address.postalCode), clean(address.city)].filter(Boolean).join(' '));
}
/**
 * The canonical one-line Swedish postadress: "Ångvägen 3, c/o X, 114 44
 * Stockholm". Same format the legacy lane writes, so a document reads the
 * same in both lanes.
 */
export function formatPostadress(address) {
    if (!address)
        return null;
    const segments = [clean(address.street), clean(address.line2), formatPostalTown(address)].filter(Boolean);
    return segments.length === 0 ? null : segments.join(', ');
}
function splitStreet(street) {
    const trimmed = street.trim().replace(/\s+/g, ' ');
    const m = trimmed.match(/^(.*?)[\s,]+(\d.*)$/);
    if (!m)
        return { name: trimmed, number: '' };
    return { name: m[1].trim(), number: m[2].trim() };
}
function renderNumbers(numbers) {
    const ints = numbers
        .filter((n) => n.kind === 'int')
        .sort((a, b) => a.value - b.value);
    const raws = numbers.filter((n) => n.kind === 'raw');
    const tokens = [];
    let i = 0;
    while (i < ints.length) {
        let j = i;
        while (j + 1 < ints.length && ints[j + 1].value === ints[j].value + 1)
            j++;
        tokens.push(j > i ? `${ints[i].text}–${ints[j].text}` : ints[i].text);
        i = j + 1;
    }
    for (const r of raws)
        tokens.push(r.text);
    return tokens.join(', ');
}
/**
 * Groups gatuadresser into compact lines: one line per street + postal
 * town, consecutive bare house numbers collapsed to an en-dash range
 * ("Storgatan 1–3, 114 44 Stockholm"), others listed ("Storgatan 1, 3, 5").
 * Mirrors the legacy lane's `groupAddressLines` exactly.
 */
export function groupAddressLines(addresses) {
    const groups = new Map();
    const order = [];
    for (const address of addresses) {
        const street = address.street?.trim();
        if (!street)
            continue;
        const { name, number } = splitStreet(street);
        if (!name)
            continue;
        const postalTown = formatPostalTown(address) ?? '';
        const key = `${name.toLowerCase()} ${postalTown.toLowerCase()}`;
        let group = groups.get(key);
        if (!group) {
            group = { name, postalTown, numbers: [] };
            groups.set(key, group);
            order.push(key);
        }
        if (number) {
            const parsed = /^\d+$/.test(number)
                ? { kind: 'int', value: Number(number), text: number }
                : { kind: 'raw', text: number };
            if (!group.numbers.some((n) => n.text === parsed.text))
                group.numbers.push(parsed);
        }
    }
    return order.map((key) => {
        const group = groups.get(key);
        const numbers = renderNumbers(group.numbers);
        const streetLine = numbers ? `${group.name} ${numbers}` : group.name;
        return group.postalTown ? `${streetLine}, ${group.postalTown}` : streetLine;
    });
}
const propertyAddresses = (property) => property.addresses && property.addresses.length > 0
    ? property.addresses
    : property.address
        ? [property.address]
        : [];
export function partyFieldValue(party, field) {
    if (!party)
        return null;
    const a = party.address ?? null;
    switch (field) {
        case 'name':
            return clean(party.name);
        case 'legalId': {
            const id = clean(party.legalId);
            return id === null ? null : formatSwedishLegalId(id);
        }
        case 'email':
            return clean(party.email);
        case 'phone':
            return clean(party.phone);
        case 'street':
            return clean(a?.street);
        case 'line2':
            return clean(a?.line2);
        case 'postalCode':
            return clean(a?.postalCode);
        case 'city':
            return clean(a?.city);
        case 'country':
            return clean(a?.country);
        case 'postalAddress':
            return formatPostalTown(a);
        case 'address':
            return formatPostadress(a);
    }
}
export function propertyFieldValue(property, field) {
    if (!property)
        return null;
    switch (field) {
        case 'designation':
            return clean(property.designation);
        case 'address':
            return formatPostadress(property.address ?? propertyAddresses(property)[0]);
        case 'municipality':
            return clean(property.municipality);
        case 'unitNumber':
            return clean(property.unitNumber);
        case 'areaSqm':
            return property.areaSqm ?? null;
        case 'roomCount':
            return property.roomCount ?? null;
        case 'floor':
            return clean(property.floor);
    }
}
export function propertyListValue(properties, field) {
    let lines;
    if (field === 'designations') {
        lines = properties.map((p) => clean(p.designation)).filter((s) => Boolean(s));
    }
    else if (field === 'summary') {
        lines = properties
            .map((p) => {
            const all = propertyAddresses(p);
            const streets = groupAddressLines(all.map((a) => ({ street: a.street ?? null })));
            const firstWithTown = all.find((a) => formatPostalTown(a));
            return [clean(p.designation), ...streets, formatPostalTown(firstWithTown)]
                .map((s) => s?.trim())
                .filter(Boolean)
                .join(', ');
        })
            .filter(Boolean);
    }
    else {
        lines = properties
            .flatMap((p) => groupAddressLines(propertyAddresses(p)))
            .map((line) => line.trim())
            .filter(Boolean);
    }
    return lines.length === 0 ? null : lines.join('\n');
}
const findParty = (parties, role, ordinal) => parties.find((p) => p.role === role && (p.ordinal ?? 0) === ordinal);
/** The value a binding produces from the given literals; null when the source is absent or empty. */
export function autofillValue(binding, sources) {
    const parties = sources.parties ?? [];
    const properties = sources.properties ?? [];
    switch (binding.kind) {
        case 'party':
            return partyFieldValue(findParty(parties, binding.role, binding.ordinal ?? 0), binding.field);
        case 'property':
            return propertyFieldValue(properties[binding.index ?? 0], binding.field);
        case 'property_list':
            return propertyListValue(properties, binding.field);
    }
}
/** Fills every auto-mode bound field from the literals. Pure. */
export function applyAutofill(input) {
    const manual = input.manual
        ? new Set(input.manual)
        : readManualKeys(input.values);
    const values = { ...input.values };
    const autoKeys = [];
    const manualKeys = [];
    const changedKeys = [];
    const seen = new Set();
    for (const field of input.fields) {
        if (!field.autofill || seen.has(field.key))
            continue;
        seen.add(field.key);
        if (manual.has(field.key)) {
            manualKeys.push(field.key);
            continue;
        }
        autoKeys.push(field.key);
        const next = autofillValue(field.autofill, input);
        if (next == null && input.missing === 'keep')
            continue;
        if (values[field.key] !== next)
            changedKeys.push(field.key);
        values[field.key] = next;
    }
    return { values, autoKeys, manualKeys, changedKeys };
}
const LEGACY_PARTY_FIELDS = {
    legal_name: 'name',
    id_number: 'legalId',
    email: 'email',
    phone: 'phone',
    address_line1: 'street',
    address_line2: 'line2',
    postal_code: 'postalCode',
    city: 'city',
    country: 'country',
    postal_address: 'postalAddress',
    composed_address: 'address',
};
const LEGACY_PROPERTY_FIELDS = {
    name: 'designation',
    address: 'address',
    kommun: 'municipality',
};
/** Translates a legacy template field's `autofill` into a slot binding. */
export function fromLegacyAutofill(source) {
    switch (source.kind) {
        case 'party':
            return { kind: 'party', role: source.role, ordinal: source.ordinal, field: LEGACY_PARTY_FIELDS[source.field] };
        case 'property':
            return { kind: 'property', field: LEGACY_PROPERTY_FIELDS[source.field] };
        case 'property_list':
            return { kind: 'property_list', field: source.field };
    }
}
//# sourceMappingURL=autofill.js.map